import type { PropsWithChildren, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CallBackProps, Step } from "react-joyride";
import Joyride, { EVENTS, STATUS } from "react-joyride";

import { AdminTourContext } from "@/contexts/AdminTourContext";
import {
  scrollSidebarToTop,
  scrollTargetIntoSidebar,
} from "@/lib/admin-tour-scroll";
import {
  ADMIN_MENU_KEYS,
  SIDEBAR_MENU_KEYS,
} from "@/providers/constants/admin-tour-keys";

const TOUR_FINISHED_STATUSES = new Set<string>([
  STATUS.FINISHED,
  STATUS.SKIPPED,
]);

const ADMIN_STEPS_START = SIDEBAR_MENU_KEYS.length;

const SIDEBAR_SELECTOR = "[data-testid='admin-sidebar'] [data-sidebar='menu']";
const ADMIN_GROUP_SELECTOR =
  "[data-testid='admin-sidebar'] [data-sidebar='group']:last-of-type [data-sidebar='menu']";

const AdminTourProvider = ({ children }: PropsWithChildren): ReactNode => {
  const { t } = useTranslation("admin");
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const steps: Step[] = useMemo(
    () => [
      ...SIDEBAR_MENU_KEYS.map(
        (key, index): Step => ({
          target: `${SIDEBAR_SELECTOR} li:nth-child(${index + 1}) a`,
          title: t(`tour.${key}_title`),
          content: t(`tour.${key}_content`),
          disableBeacon: true,
          placement: "right" as const,
        }),
      ),
      ...ADMIN_MENU_KEYS.map(
        (key, index): Step => ({
          target: `${ADMIN_GROUP_SELECTOR} li:nth-child(${index + 1}) a`,
          title: t(`tour.${key}_title`),
          content: t(`tour.${key}_content`),
          disableBeacon: true,
          // NOTE: "right-end" anchors the tooltip bottom to the target bottom so
          // it opens upward — preventing overflow for items near the screen bottom.
          placement: "right-end" as const,
        }),
      ),
      {
        target: "[data-testid='admin-breadcrumb-home']",
        title: t("tour.breadcrumbs_title"),
        content: t("tour.breadcrumbs_content"),
        disableBeacon: true,
        placement: "bottom" as const,
      },
      {
        target: "[data-testid='admin-user-menu']",
        title: t("tour.user_menu_title"),
        content: t("tour.user_menu_content"),
        disableBeacon: true,
        placement: "bottom-end" as const,
      },
    ],
    [t],
  );

  const startTour = (): void => {
    scrollSidebarToTop();
    setIsRunning(true);
    setCurrentStep(0);
  };

  const handleJoyrideCallback = (data: CallBackProps): void => {
    const { status, action, index, type } = data;

    setCurrentStep(index);

    // NOTE: react-joyride emits STEP_BEFORE only on the initial transition into
    // the running tour, not on every step. TOOLTIP fires when each step's tooltip
    // opens, which is when we need to adjust sidebar scroll position.
    if (type === EVENTS.TOOLTIP) {
      if (index >= ADMIN_STEPS_START) {
        const step = steps[index];
        if (typeof step?.target === "string") {
          scrollTargetIntoSidebar(step.target);
        }
      } else {
        scrollSidebarToTop();
      }
    }

    if (TOUR_FINISHED_STATUSES.has(status) || action === "close") {
      setIsRunning(false);
      scrollSidebarToTop();
    }
  };

  const contextValue = useMemo(
    () => ({ startTour, currentStep, isRunning }),
    [currentStep, isRunning],
  );

  return (
    <AdminTourContext.Provider value={contextValue}>
      {children}

      <Joyride
        key={isRunning ? "running" : "not-running"}
        steps={steps}
        run={isRunning}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep={false}
        disableScrolling
        spotlightPadding={5}
        disableOverlayClose={false}
        disableCloseOnEsc
        disableScrollParentFix={false}
        styles={{
          options: {
            arrowColor: "hsl(var(--background))",
            backgroundColor: "hsl(var(--background))",
            primaryColor: "hsl(var(--primary))",
            textColor: "hsl(var(--foreground))",
            zIndex: 1000,
          },
          tooltip: {
            padding: "1.25rem",
            width: "450px",
            borderRadius: "calc(var(--radius) - 2px)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          },
          tooltipContainer: {
            textAlign: "start" as const,
          },
          tooltipTitle: {
            fontSize: "1.125rem",
            lineHeight: "1.75rem",
            fontWeight: "600",
            marginBottom: "8px",
          },
          tooltipContent: {
            fontSize: "1rem",
            lineHeight: "1.5rem",
            padding: "0",
          },
          buttonNext: {
            backgroundColor: "hsl(var(--primary-dark))",
            borderRadius: "calc(var(--radius) - 2px)",
            padding: "0.5rem 1rem",
            height: "2.5rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
          },
          buttonBack: {
            padding: "0",
            color: "var(--muted-foreground)",
            marginInlineEnd: "1rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
          },
          buttonSkip: {
            padding: "0",
            color: "var(--muted-foreground)",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
          },
          buttonClose: {
            display: "none",
          },
        }}
        floaterProps={{
          hideArrow: false,
          disableAnimation: true,
          placement: "auto",
          styles: {
            arrow: {
              length: 8,
              spread: 12,
            },
          },
        }}
        locale={{
          back: t("tour.back"),
          close: t("tour.close"),
          last: t("tour.last"),
          next: t("tour.next"),
          skip: t("tour.skip"),
        }}
        callback={handleJoyrideCallback}
      />
    </AdminTourContext.Provider>
  );
};

export default AdminTourProvider;
